import type { CallMedia, Person } from "../../shared/types";

// What a call is doing right now. Only "starting" has no id: the server has not
// created the call yet. "calling" and "incoming" are the two sides of a ring.
export type CallStatus =
  | "starting"
  | "calling"
  | "incoming"
  | "connecting"
  | "connected"
  | "reconnecting";

export type CallStage =
  | { status: "starting"; id: null }
  | { status: Exclude<CallStatus, "starting">; id: string };

export type Call = CallStage & {
  person: Person;
  media: CallMedia;
  supportsScreenSharing: boolean;
  seconds: number;
  muted: boolean;
  microphoneStopped: boolean;
  microphoneBusy: boolean;
  level: number;
  deafened: boolean;
  playbackBlocked: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  remoteAudio: MediaStream | null;
  cameraOff: boolean;
  cameraBusy: boolean;
  sharingScreen: boolean;
  remoteCameraOff: boolean;
  remoteMuted: boolean;
  remoteSharingScreen: boolean;
  canSendVideo: boolean;
  canFlipCamera: boolean;
  canShareScreen: boolean;
};

// What stays on screen of an answered call once its media is gone.
export type EndedCall = {
  person: Person;
  media: CallMedia;
  seconds: number;
};

export const isRinging = (call: { status: CallStatus } | null) =>
  call?.status === "calling" || call?.status === "incoming";

export const isTalking = (call: { status: CallStatus } | null) =>
  call?.status === "connected" || call?.status === "reconnecting";

export const isAnswered = <T extends { status: CallStatus }>(
  call: T | null,
): call is T & { status: "connecting" | "connected" | "reconnecting" } =>
  call?.status === "connecting" || isTalking(call);
